import { memo, useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput, IInputFile, useFormContext } from "@4uhub/lib4uhub";

import { TRegisterForm } from "../RegisterSchema";
import {
  SITE_APP_TYPE_EXTERNAL_SITE,
  isChatProfileEnabled,
} from "../SiteAppType";
import SiteAppChatProfilePreview from "./SiteAppChatProfilePreview";
import SiteAppChatProfileFile from "./SiteAppChatProfileFile";

export const CHAT_LOGO_MEDIA_TYPE_CODE = "3";
export const CHAT_BOT_AVATAR_MEDIA_TYPE_CODE = "4";

export const DEFAULT_CHAT_PRIMARY_COLOR = "#ffffff";
export const DEFAULT_CHAT_SECONDARY_COLOR = "#1991D2";

// Tamanho padrão do widget (30em x 40em com fonte de 16px)
export const DEFAULT_CHAT_WIDTH = 480;
export const DEFAULT_CHAT_HEIGHT = 640;

const translationPath = "page.register.applications.";

interface ISiteAppChatProfileProps {
  onDeleteFileHandler: (file: IInputFile) => void;
}

const SiteAppChatProfile = ({
  onDeleteFileHandler,
}: ISiteAppChatProfileProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<TRegisterForm>();

  const type = useWatch({ name: "type", control });
  const chatTicketService = useWatch({ name: "chatTicketService", control });

  const isEnabled = isChatProfileEnabled(type?.code, chatTicketService);
  const isExternalSite = type?.code === SITE_APP_TYPE_EXTERNAL_SITE;

  useEffect(() => {
    if (!isEnabled) return;

    if (!getValues("chatWidth")) {
      setValue("chatWidth", DEFAULT_CHAT_WIDTH);
    }
    if (!getValues("chatHeight")) {
      setValue("chatHeight", DEFAULT_CHAT_HEIGHT);
    }

    if (!isExternalSite) return;

    if (!getValues("chatPrimaryColor")) {
      setValue("chatPrimaryColor", DEFAULT_CHAT_PRIMARY_COLOR);
    }
    if (!getValues("chatSecondaryColor")) {
      setValue("chatSecondaryColor", DEFAULT_CHAT_SECONDARY_COLOR);
    }
  }, [isEnabled, isExternalSite, getValues, setValue]);

  if (!isEnabled) return null;

  return (
    <Grid item xs={12} sx={{ mb: isExternalSite ? 0 : 6 }}>
      <Box
        sx={{
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "grey.900" : "#f4f5f9",
          "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
        }}
      >
        <Typography variant="h6" mb={2}>
          {t(translationPath + "chat_profile")}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="chatTitle"
              fullWidth
              label={t(translationPath + "chat_title")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="chatSubtitle"
              fullWidth
              label={t(translationPath + "chat_subtitle")}
            />
          </Grid>

          <Grid item xs={12}>
            <FormInput
              size="small"
              name="chatBotName"
              fullWidth
              label={t(translationPath + "chat_bot_name")}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormInput
                  size="small"
                  name="chatWidth"
                  type="number"
                  fullWidth
                  label={t(translationPath + "chat_width")}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormInput
                  size="small"
                  name="chatHeight"
                  type="number"
                  fullWidth
                  label={t(translationPath + "chat_height")}
                />
              </Grid>
            </Grid>
          </Grid>

          {isExternalSite && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormInput
                    size="small"
                    name="chatPrimaryColor"
                    type="color"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& input": { height: 28, py: 0.75, cursor: "pointer" },
                    }}
                    label={t(translationPath + "chat_primary_color")}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormInput
                    size="small"
                    name="chatSecondaryColor"
                    type="color"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& input": { height: 28, py: 0.75, cursor: "pointer" },
                    }}
                    label={t(translationPath + "chat_secondary_color")}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <SiteAppChatProfileFile
              name="chatLogo"
              label={t(translationPath + "chat_logo")}
              placeholder="Logo"
              mediaTypeCode={CHAT_LOGO_MEDIA_TYPE_CODE}
              variant="rounded"
              onDeleteFileHandler={onDeleteFileHandler}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <SiteAppChatProfileFile
              name="chatBotAvatar"
              label={t(translationPath + "chat_bot_avatar")}
              placeholder="Bot"
              mediaTypeCode={CHAT_BOT_AVATAR_MEDIA_TYPE_CODE}
              variant="circular"
              onDeleteFileHandler={onDeleteFileHandler}
            />
          </Grid>

          <Grid item xs={12}>
            <SiteAppChatProfilePreview isExternalSite={isExternalSite} />
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default memo(SiteAppChatProfile);
